/**
 * What the review of a run knows, keyed by the run's requestId: a thread can hold several
 * AI replies, each its own run, and the account-wide socket broadcast is filtered by that
 * key. Memory only; a verdict is run output and goes with the thread.
 */

import { atom } from 'jotai';
import type { PlaygroundJudgeResult } from '@/services/ai-function.service';
import { replySideAtom } from './reply-side';

export interface InvestigationStep {
  text: string;
  /** Brahma's own clock, when the step carried one. */
  ts: string | null;
}

type JudgeState =
  | { status: 'idle' }
  | { status: 'judging' }
  /** `authored`: the reply was typed here, not read off the ticket, so the screen must not call it "what was actually sent". */
  | { status: 'done'; result: PlaygroundJudgeResult; authored: boolean }
  | { status: 'failed'; message: string };

type InvestigationState =
  | { status: 'idle' }
  | { status: 'running'; steps: InvestigationStep[] }
  | { status: 'done'; steps: InvestigationStep[]; investigation: Record<string, unknown> }
  | { status: 'failed'; steps: InvestigationStep[]; reason: string };

export interface RunReview {
  judge: JudgeState;
  investigation: InvestigationState;
}

export const EMPTY_REVIEW: RunReview = { judge: { status: 'idle' }, investigation: { status: 'idle' } };

export const runReviewsAtom = atom<Record<string, RunReview>>({});

const withRun = (
  reviews: Record<string, RunReview>,
  requestId: string,
  change: (review: RunReview) => RunReview
): Record<string, RunReview> => ({
  ...reviews,
  [requestId]: change(reviews[requestId] ?? EMPTY_REVIEW),
});

export const setJudgeStateAtom = atom(null, (get, set, requestId: string, judge: JudgeState) => {
  set(runReviewsAtom, withRun(get(runReviewsAtom), requestId, (review) => ({ ...review, judge })));
});

export const setInvestigationStateAtom = atom(
  null,
  (get, set, requestId: string, investigation: InvestigationState) => {
    set(runReviewsAtom, withRun(get(runReviewsAtom), requestId, (review) => ({ ...review, investigation })));
  }
);

/** Steps carry no sequence number, so a consecutive repeat is a resend, not a second milestone. */
const appendStep = (steps: InvestigationStep[], step: InvestigationStep): InvestigationStep[] =>
  steps[steps.length - 1]?.text === step.text ? steps : [...steps, step];

/** A step for a run whose investigation is not running is dropped: a late or foreign broadcast must not resurrect or start a trail. */
export const appendInvestigationStepAtom = atom(
  null,
  (get, set, requestId: string, step: InvestigationStep) => {
    const current = get(runReviewsAtom)[requestId]?.investigation;
    if (current?.status !== 'running') return;
    set(setInvestigationStateAtom, requestId, { status: 'running', steps: appendStep(current.steps, step) });
  }
);

/** The trail is kept beside the verdict: how it got there is part of the answer. */
export const concludeInvestigationAtom = atom(
  null,
  (get, set, requestId: string, outcome: { investigation: Record<string, unknown> | null; reason: string | null }) => {
    const current = get(runReviewsAtom)[requestId]?.investigation;
    if (current?.status !== 'running') return;
    set(
      setInvestigationStateAtom,
      requestId,
      outcome.investigation
        ? { status: 'done', steps: current.steps, investigation: outcome.investigation }
        : { status: 'failed', steps: current.steps, reason: outcome.reason ?? 'error' }
    );
  }
);

/** Reviews go with the thread: the reset that empties it empties these, and the reply switches with them. */
export const clearRunReviewsAtom = atom(null, (_get, set) => {
  set(runReviewsAtom, {});
  set(replySideAtom, {});
});
